import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { X } from 'lucide-react';

import styles from './Walkthrough.module.scss';

const HIGHLIGHT_PADDING = 10;
const POPOVER_WIDTH = 320;
const POPOVER_OFFSET = 8;
const VIEWPORT_PADDING = 16;
const POPOVER_HEIGHT = 210;
const ARROW_PADDING = 16;
const MOBILE_BREAKPOINT = 768;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTargets(step) {
  if (!step || typeof document === 'undefined') {
    return [];
  }

  return step.multiTarget
    ? Array.from(document.querySelectorAll(step.target))
    : [document.querySelector(step.target)];
}

function getTargetRect(step) {
  const targets = getTargets(step);

  const rects = targets
    .filter(Boolean)
    .map((target) => target.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);

  if (!rects.length) {
    return null;
  }

  return rects.reduce(
    (bounds, rect) => ({
      top: Math.min(bounds.top, rect.top),
      right: Math.max(bounds.right, rect.right),
      bottom: Math.max(bounds.bottom, rect.bottom),
      left: Math.min(bounds.left, rect.left),
      width:
        Math.max(bounds.right, rect.right) - Math.min(bounds.left, rect.left),
      height:
        Math.max(bounds.bottom, rect.bottom) - Math.min(bounds.top, rect.top),
    }),
    rects[0]
  );
}

function scrollTargetIntoView(step) {
  if (typeof window === 'undefined' || window.innerWidth > MOBILE_BREAKPOINT) {
    return;
  }

  const targets = getTargets(step).filter(Boolean);

  if (!targets.length) {
    return;
  }

  const target = step.multiTarget
    ? targets[Math.floor(targets.length / 2)]
    : targets[0];
  const scrollContainer = target.closest('[data-walkthrough-scroll-container]');

  if (scrollContainer) {
    const targetRect = target.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const targetCenterOffset =
      targetRect.left -
      containerRect.left +
      scrollContainer.scrollLeft +
      targetRect.width / 2;

    scrollContainer.scrollTo({
      left: targetCenterOffset - scrollContainer.clientWidth / 2,
      behavior: 'smooth',
    });

    return;
  }

  target.scrollIntoView({
    behavior: 'smooth',
    block: step.id === 'applicant-card' ? 'center' : 'nearest',
    inline: 'center',
  });
}

function getResolvedPlacement(rect, placement) {
  if (typeof window === 'undefined' || !rect) {
    return placement;
  }

  if (
    window.innerWidth <= MOBILE_BREAKPOINT &&
    ['left', 'right'].includes(placement)
  ) {
    return 'bottom';
  }

  if (placement === 'left' && rect.left < POPOVER_WIDTH + POPOVER_OFFSET) {
    return 'bottom';
  }

  if (
    placement === 'right' &&
    window.innerWidth - rect.right < POPOVER_WIDTH + POPOVER_OFFSET
  ) {
    return 'bottom';
  }

  return placement;
}

function getPopoverPosition(rect, placement) {
  if (typeof window === 'undefined' || !rect) {
    return {};
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxLeft = viewportWidth - POPOVER_WIDTH - VIEWPORT_PADDING;
  const centeredLeft = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;

  if (placement === 'left') {
    return {
      left: clamp(
        rect.left - POPOVER_WIDTH - POPOVER_OFFSET,
        VIEWPORT_PADDING,
        maxLeft
      ),
      top: clamp(
        rect.top + rect.height / 2 - POPOVER_HEIGHT / 2,
        VIEWPORT_PADDING,
        viewportHeight - POPOVER_HEIGHT - VIEWPORT_PADDING
      ),
    };
  }

  if (placement === 'right') {
    return {
      left: clamp(rect.right + POPOVER_OFFSET, VIEWPORT_PADDING, maxLeft),
      top: clamp(
        rect.top + rect.height / 2 - POPOVER_HEIGHT / 2,
        VIEWPORT_PADDING,
        viewportHeight - POPOVER_HEIGHT - VIEWPORT_PADDING
      ),
    };
  }

  if (placement === 'top') {
    return {
      left: clamp(centeredLeft, VIEWPORT_PADDING, maxLeft),
      top: clamp(
        rect.top - POPOVER_HEIGHT - POPOVER_OFFSET,
        VIEWPORT_PADDING,
        viewportHeight - POPOVER_HEIGHT - VIEWPORT_PADDING
      ),
    };
  }

  return {
    left: clamp(centeredLeft, VIEWPORT_PADDING, maxLeft),
    top: clamp(
      rect.bottom + POPOVER_OFFSET,
      VIEWPORT_PADDING,
      viewportHeight - POPOVER_HEIGHT - VIEWPORT_PADDING
    ),
  };
}

function getArrowPosition(rect, popoverPosition, placement) {
  const targetCenterX = rect.left + rect.width / 2;
  const targetCenterY = rect.top + rect.height / 2;

  if (placement === 'left' || placement === 'right') {
    return {
      '--walkthrough-arrow-top': `${clamp(
        targetCenterY - popoverPosition.top,
        ARROW_PADDING,
        POPOVER_HEIGHT - ARROW_PADDING
      )}px`,
    };
  }

  return {
    '--walkthrough-arrow-left': `${clamp(
      targetCenterX - popoverPosition.left,
      ARROW_PADDING,
      POPOVER_WIDTH - ARROW_PADDING
    )}px`,
  };
}

const Walkthrough = ({ isOpen, steps, onComplete, onSkip, onStepChange }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const measureFrameRef = useRef(null);
  const hasNotifiedOpenStepRef = useRef(false);

  const currentStep = steps[currentStepIndex];
  const paddedRect = useMemo(() => {
    if (!targetRect) {
      return null;
    }

    const viewportWidth =
      typeof window === 'undefined' ? targetRect.right : window.innerWidth;
    const viewportHeight =
      typeof window === 'undefined' ? targetRect.bottom : window.innerHeight;
    const top = Math.max(targetRect.top - HIGHLIGHT_PADDING, 0);
    const left = Math.max(targetRect.left - HIGHLIGHT_PADDING, 0);
    const right = Math.min(targetRect.right + HIGHLIGHT_PADDING, viewportWidth);
    const bottom = Math.min(
      targetRect.bottom + HIGHLIGHT_PADDING,
      viewportHeight
    );

    return {
      top,
      left,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  }, [targetRect]);

  const measureCurrentStep = useCallback(() => {
    if (!isOpen || !steps.length) {
      return;
    }

    const rect = getTargetRect(currentStep);

    if (rect) {
      setTargetRect(rect);
      return;
    }

    const nextAvailableIndex = steps.findIndex((step, index) => {
      return index > currentStepIndex && getTargetRect(step);
    });

    if (nextAvailableIndex !== -1) {
      onStepChange(steps[nextAvailableIndex], nextAvailableIndex);
      setCurrentStepIndex(nextAvailableIndex);
      return;
    }

    setTargetRect(null);
  }, [currentStep, currentStepIndex, isOpen, onStepChange, steps]);

  useEffect(() => {
    if (!isOpen) {
      hasNotifiedOpenStepRef.current = false;
      return;
    }

    if (hasNotifiedOpenStepRef.current || !currentStep) {
      return;
    }

    hasNotifiedOpenStepRef.current = true;
    onStepChange(currentStep, currentStepIndex);
  }, [currentStep, currentStepIndex, isOpen, onStepChange]);

  const cancelScheduledMeasure = useCallback(() => {
    if (measureFrameRef.current === null) {
      return;
    }

    window.cancelAnimationFrame(measureFrameRef.current);
    measureFrameRef.current = null;
  }, []);

  const scheduleMeasureCurrentStep = useCallback(() => {
    cancelScheduledMeasure();

    measureFrameRef.current = window.requestAnimationFrame(() => {
      measureFrameRef.current = null;
      measureCurrentStep();
    });
  }, [cancelScheduledMeasure, measureCurrentStep]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    scrollTargetIntoView(currentStep);

    scheduleMeasureCurrentStep();

    return cancelScheduledMeasure;
  }, [cancelScheduledMeasure, currentStep, isOpen, scheduleMeasureCurrentStep]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStepIndex(0);
      setTargetRect(null);
      return undefined;
    }

    scheduleMeasureCurrentStep();
    const handleReposition = () => scheduleMeasureCurrentStep();

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      cancelScheduledMeasure();
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [cancelScheduledMeasure, isOpen, scheduleMeasureCurrentStep]);

  useEffect(() => {
    if (!isOpen || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const targets = getTargets(currentStep).filter(Boolean);

    if (!targets.length) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleMeasureCurrentStep();
    });

    targets.forEach((target) => resizeObserver.observe(target));

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentStep, isOpen, scheduleMeasureCurrentStep]);

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  const handleNext = () => {
    if (currentStepIndex >= steps.length - 1) {
      handleComplete();
      return;
    }

    const nextStepIndex = currentStepIndex + 1;
    onStepChange(steps[nextStepIndex], nextStepIndex);
    setCurrentStepIndex(nextStepIndex);
  };

  if (!isOpen || !currentStep || !paddedRect) {
    return null;
  }

  const placement = getResolvedPlacement(
    paddedRect,
    currentStep?.placement || 'bottom'
  );
  const popoverPosition = getPopoverPosition(paddedRect, placement);
  const arrowPosition = getArrowPosition(
    paddedRect,
    popoverPosition,
    placement
  );
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className={styles.walkthrough} aria-live="polite">
      <button
        type="button"
        className={styles.backdrop}
        onClick={handleSkip}
        aria-label="Skip tour"
      />

      <div
        className={styles.highlight}
        style={{
          top: paddedRect.top,
          left: paddedRect.left,
          width: paddedRect.width,
          height: paddedRect.height,
        }}
      />

      <div
        className={`${styles.popover} ${styles[placement]}`}
        style={{
          ...popoverPosition,
          ...arrowPosition,
        }}
        role="dialog"
        aria-label={currentStep.title}
      >
        <div className={styles.popoverHeader}>
          <span className={styles.stepCount}>
            Step {currentStepIndex + 1} of {steps.length}
          </span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleSkip}
            aria-label="Skip tour"
          >
            <X width={16} height={16} />
          </button>
        </div>
        <h4 className={styles.title}>{currentStep.title}</h4>
        <p className={styles.body}>{currentStep.body}</p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.skipButton}
            onClick={handleSkip}
          >
            Skip Tour
          </button>
          <button
            type="button"
            className={styles.nextButton}
            onClick={handleNext}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

Walkthrough.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      target: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
      multiTarget: PropTypes.bool,
    })
  ).isRequired,
  onComplete: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
};

Walkthrough.defaultProps = {
  onStepChange: () => {},
};

export default Walkthrough;
